<template>
  <el-dialog
    title="视频列表"
    :visible.sync="dialogVisible"
    width="900px"
    :close-on-click-modal="false"
    class="videoListDialog"
  >
    <div class="subjectName">{{ rowData.examSubjectName }}</div>
    <el-table :data="tableData" border style="width: 100%" v-loading="listLoading">
      <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
      <el-table-column label="操作" width="160" align="center">
        <template slot-scope="scope">
          <el-button v-if="scope.row.url" type="text" @click="handleView(scope.row)"
            >查看</el-button
          >
          <el-button type="text" @click="handleUpload(scope.row)">上传</el-button>
        </template>
      </el-table-column>
      <el-table-column prop="uploader" label="上传人" align="center"></el-table-column>
      <el-table-column prop="markType" label="阅卷类型" align="center"></el-table-column>
      <el-table-column prop="startTime" label="开始时间" align="center"></el-table-column>
      <el-table-column prop="endTime" label="结束时间" align="center"></el-table-column>
    </el-table>
    <span slot="footer">
      <el-button type="primary" @click="dialogVisible = false">关 闭</el-button>
    </span>

    <!-- 上传弹框 -->
    <el-dialog
      title="上传"
      :visible.sync="uploadVisible"
      width="520px"
      append-to-body
      custom-class="uploadDialog"
      :close-on-click-modal="false"
    >
      <div class="uploadForm">
        <div class="formItem">
          <span class="label">上传人：</span>
          <span class="value">{{ userInfo.userName }}</span>
          <span class="label">阅卷类型：</span>
          <span class="value">{{ markModeName || '—' }}</span>
        </div>
        <div class="formItem">
          <span class="label">文件：</span>
          <el-upload
            ref="upload"
            action=""
            accept="video/*,.mp4,.webm"
            :limit="1"
            :auto-upload="false"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
          >
            <el-button size="small">选择文件</el-button>
          </el-upload>
        </div>
        <div class="formItem">
          <span class="label">时间：</span>
          <el-date-picker
            v-model="timeRange"
            type="datetimerange"
            range-separator="——"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
          ></el-date-picker>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="uploadVisible = false">取 消</el-button>
        <el-button type="primary" :loading="uploading" @click="handleUploadConfirm"
          >确 定</el-button
        >
      </span>
    </el-dialog>

    <!-- 视频播放弹框 -->
    <el-dialog
      title="查看视频"
      :visible.sync="playVisible"
      width="720px"
      append-to-body
      custom-class="playDialog"
      :close-on-click-modal="false"
      @close="handlePlayClose"
    >
      <video v-if="playVisible" :src="currentUrl" controls autoplay class="videoPlayer"></video>
    </el-dialog>
  </el-dialog>
</template>
<script>
import to from 'await-to'
import { mapGetters } from 'vuex'
import {
  markingVideoList,
  markingVideoDetail,
  markingVideoAdd,
  markingVideoEdit,
  subjectMarkingTeachers,
} from '@/api/exam'
import { tencentUpload } from '@/api/login'

// 阅卷模式文本
const MARK_MODE_MAP = { 1: '单人阅卷', 2: '分题段阅卷', 3: '重复阅卷' }

// 从文件名解析时间，例：20260314121223-20260314122321.mp4
function parseTimeFromFileName(fileName) {
  const match = String(fileName).match(/(\d{14})-(\d{14})/)
  const format = s =>
    `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(
      10,
      12,
    )}:${s.slice(12, 14)}`
  return match ? { startTime: format(match[1]), endTime: format(match[2]) } : {}
}

export default {
  name: 'VideoListDialog',
  data() {
    return {
      dialogVisible: false,
      uploadVisible: false,
      playVisible: false,
      currentUrl: '',
      rowData: {},
      videoList: [],
      listLoading: false,
      uploadFile: null,
      timeRange: [],
      uploading: false,
      uploadRow: null, // 正在编辑的视频行，null 表示新增上传
      markMode: undefined,
      markModeName: '',
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    // 末尾追加一行空数据，作为上传入口
    tableData() {
      return [...this.videoList, { isEmpty: true }]
    },
  },
  methods: {
    show(row) {
      this.rowData = row
      this.dialogVisible = true
      this.loadList()
      this.loadMarkMode()
    },
    // 阅卷视频列表
    async loadList() {
      this.listLoading = true
      const [res, err] = await to(
        markingVideoList({
          examSubjectId: this.rowData.id,
          examPlanId: this.rowData.planId,
        }),
      )
      this.listLoading = false
      if (err) return this.$message.warning(err.msg)
      this.videoList = (res.data || []).map(v => ({
        ...v,
        url: v.videoUrl,
        uploader: v.userName,
        markType: MARK_MODE_MAP[v.markMode] || '',
        startTime: v.startTime || '',
        endTime: v.endTime || '',
      }))
    },
    // 查询该科目的阅卷方式（上传时需传 markMode）
    async loadMarkMode() {
      this.markMode = undefined
      this.markModeName = ''
      const [res] = await to(subjectMarkingTeachers({ subjectId: this.rowData.id }))
      if (res && res.data) {
        this.markMode = res.data.markMode
        this.markModeName = MARK_MODE_MAP[res.data.markMode] || ''
      }
    },
    async handleView(row) {
      const [res, err] = await to(markingVideoDetail(row.id))
      if (err || !res.data || !res.data.videoUrl) {
        if (!row.url) return this.$message.warning(err && err.msg ? err.msg : '未获取到视频地址')
      }
      this.currentUrl = (res.data && res.data.videoUrl) || row.url
      this.playVisible = true
    },
    handlePlayClose() {
      this.currentUrl = ''
    },
    // 空数据行为新增上传，普通行为编辑已有视频
    handleUpload(row) {
      this.uploadRow = row && !row.isEmpty ? row : null
      this.uploadFile = null
      // 编辑时回填已有时间
      this.timeRange =
        this.uploadRow && this.uploadRow.startTime
          ? [this.uploadRow.startTime, this.uploadRow.endTime || '']
          : []
      if (this.$refs.upload) this.$refs.upload.clearFiles()
      this.uploadVisible = true
    },
    handleFileChange(file) {
      this.uploadFile = file.raw
      // 从文件名解析开始/结束时间，解析成功自动填充，失败则留空
      const { startTime, endTime } = parseTimeFromFileName(file.name)
      this.timeRange = startTime ? [startTime, endTime] : []
    },
    handleFileRemove() {
      this.uploadFile = null
      this.timeRange = []
    },
    // 上传视频文件：空数据行调新增接口，普通行调编辑接口
    async handleUploadConfirm() {
      if (!this.uploadFile) return this.$message.warning('请选择文件')
      this.uploading = true
      const params = new FormData()
      params.append('file', this.uploadFile)
      const [upRes, upErr] = await to(tencentUpload(params))
      if (upErr) {
        this.uploading = false
        return this.$message.warning(upErr.msg)
      }
      const request = this.uploadRow
        ? markingVideoEdit({
            id: this.uploadRow.id,
            fileName: this.uploadFile.name,
            videoUrl: upRes.data,
            startTime: this.timeRange[0] || '',
            endTime: this.timeRange[1] || '',
          })
        : markingVideoAdd({
            examPaperId: this.rowData.paperId,
            examPlanId: this.rowData.planId,
            examSubjectId: this.rowData.id,
            fileName: this.uploadFile.name,
            markMode: this.markMode,
            videoUrl: upRes.data,
            startTime: this.timeRange[0] || '',
            endTime: this.timeRange[1] || '',
          })
      const [, err] = await to(request)
      this.uploading = false
      if (err) return this.$message.warning(err.msg)
      this.$message.success(this.uploadRow ? '修改成功' : '上传成功')
      this.uploadVisible = false
      this.loadList()
    },
  },
}
</script>
<style scoped lang="scss">
.videoListDialog {
  .subjectName {
    text-align: center;
    font-size: 16px;
    font-weight: 500;
    color: #333333;
    margin-bottom: 16px;
  }
}
</style>
<style lang="scss">
// 上传/播放弹框使用 append-to-body 挂载在 body 下，脱离外层弹窗，scoped 样式无法作用到，需用全局样式
.uploadDialog {
  .uploadForm {
    .formItem {
      display: flex;
      align-items: flex-start;
      margin-bottom: 18px;
      .label {
        flex-shrink: 0;
        width: 80px;
        line-height: 32px;
        color: #333333;
      }
      .value {
        flex: 1;
        line-height: 32px;
        color: #606266;
        word-break: break-all;
      }
    }
  }
}
.playDialog {
  .videoPlayer {
    display: block;
    width: 100%;
    max-height: 60vh;
    background: #000;
  }
}
</style>
